import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS, GALLERY } from "../../data/content";
import Reveal from "./Reveal";

function resolve(path) {
  // path like "yacht.sunsetWide" or "atmospheric.interior"
  const parts = path.split(".");
  let v = ASSETS;
  for (const p of parts) v = v?.[p];
  return v;
}

export default function Gallery() {
  const trackRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start start", "end end"],
  });
  // Horizontal translate as user scrolls vertically
  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-78%"]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative bg-[#050B14]"
    >
      {/* Intro header */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-24 md:pt-40 pb-16">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <div className="h-px w-12 bg-[#E5D3B3]" />
                <span className="overline">Cinematic Gallery</span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2
                data-testid="gallery-headline"
                className="font-serif-display text-white text-[clamp(2.25rem,5vw,4.5rem)] leading-[1.05] font-light tracking-[-0.02em]"
              >
                Nine vantages of
                <br />a quiet revolution.
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.25}>
              <p className="text-[#D4C9A8] text-base font-light leading-relaxed">
                Scroll to drift along MARIE-JOSEPH — deck to dusk.
              </p>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Horizontal scroll track */}
      <div ref={trackRef} className="relative h-[320vh]">
        <div className="sticky top-0 h-screen overflow-hidden">
          <motion.div
            style={{ x }}
            className="flex h-full items-center gap-6 md:gap-10 pl-6 md:pl-12 will-change-transform"
          >
            {GALLERY.map((g, i) => (
              <figure
                key={g.key}
                data-testid={`gallery-card-${g.key}`}
                className="relative flex-shrink-0 h-[70vh] md:h-[78vh] w-[78vw] md:w-[56vw] lg:w-[42vw] overflow-hidden"
              >
                <img
                  src={resolve(g.img)}
                  alt={`MARIE-JOSEPH — ${g.label}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/85 via-[#050B14]/10 to-transparent" />
                <figcaption className="absolute bottom-8 left-8 right-8 flex items-end justify-between">
                  <div>
                    <div className="overline text-[10px] text-white/60 mb-3">
                      0{i + 1} / {GALLERY.length}
                    </div>
                    <div className="font-serif-display text-white text-3xl md:text-4xl font-light tracking-tight">
                      {g.label}
                    </div>
                  </div>
                </figcaption>
                <div className="absolute top-0 left-0 right-0 h-px bg-white/10" />
                <div className="absolute bottom-0 left-0 right-0 h-px bg-white/10" />
              </figure>
            ))}
            {/* End spacer */}
            <div className="flex-shrink-0 w-[20vw]" />
          </motion.div>

          {/* Progress hairline */}
          <div className="absolute bottom-8 left-6 right-6 md:left-12 md:right-12 h-px bg-white/10">
            <motion.div
              style={{ scaleX: scrollYProgress, originX: 0 }}
              className="h-px bg-[#E5D3B3]"
            />
          </div>

          {/* Hint */}
          <div className="absolute top-8 right-6 md:right-12 overline text-white/40 text-[10px]">
            Scroll →
          </div>
        </div>
      </div>
    </section>
  );
}
